(function () {
	'use strict';

	/* ---------------------------------------------------------------------
	 * Storage helpers — sessionStorage throws in Safari private mode and when
	 * cookies are blocked, so every access has to be guarded.
	 * ------------------------------------------------------------------- */
	const store = {
		get(key) {
			try { return sessionStorage.getItem(key); } catch (e) { return null; }
		},
		set(key, value) {
			try { sessionStorage.setItem(key, value); } catch (e) { /* full or blocked */ }
		},
		remove(key) {
			try { sessionStorage.removeItem(key); } catch (e) { /* blocked */ }
		},
	};

	// Params that differ between visits (ads, email, Shopify search redirects)
	// must not be part of the key, or the page won't recognise itself on return.
	const VOLATILE_PARAMS = /^(utm_|fbclid|gclid|gbraid|wbraid|ttclid|msclkid|_kx|srsltid|_gl|mc_cid|mc_eid)/i;

	const pageKey = () => {
		try {
			const url = new URL(window.location.href);
			[...url.searchParams.keys()].forEach(key => {
				if (VOLATILE_PARAMS.test(key)) url.searchParams.delete(key);
			});
			return url.pathname + url.search;
		} catch (e) {
			return window.location.pathname;
		}
	};

	const productPath = href => {
		try { return new URL(href, window.location.origin).pathname; } catch (e) { return href; }
	};

	const KEY = pageKey();
	const SCROLL_KEY = 'scroll:' + KEY;
	const RETURN_KEY = 'return-to-product:' + KEY;

	// This file loads on every page of the theme. Switching scroll restoration
	// to 'manual' disables the browser's own back-button behaviour site-wide,
	// so only do it once we know we can actually store a position ourselves —
	// otherwise a visitor with blocked storage lands at the top of every page
	// they navigate back to, on every template.
	const STORAGE_AVAILABLE = (() => {
		try {
			sessionStorage.setItem('__scroll_probe__', '1');
			sessionStorage.removeItem('__scroll_probe__');
			return true;
		} catch (e) {
			return false;
		}
	})();

	if (!STORAGE_AVAILABLE) return;

	if ('scrollRestoration' in history) {
		history.scrollRestoration = 'manual';
	}

	// Restoring must only happen when the visitor actually came back. Opening a
	// collection fresh from the menu and being dumped into the middle of a list
	// from an hour ago reads as a bug, not a feature.
	// Set before a bfcache-driven restore: `performance.navigation` still
	// describes the *original* load of this document, so it cannot be trusted
	// to recognise a back navigation once the page has been cached and revived.
	let cameFromCache = false;

	const isReturnNavigation = () => {
		if (cameFromCache) return true;
		try {
			const entry = performance.getEntriesByType('navigation')[0];
			if (entry && entry.type) return entry.type === 'back_forward' || entry.type === 'reload';
		} catch (e) { /* Navigation Timing L2 unsupported */ }
		return true; // cannot tell — keep restoring
	};

	/* ---------------------------------------------------------------------
	 * Save scroll position. `pagehide` and `visibilitychange` fire on iOS
	 * Safari where `beforeunload` does not, and neither blocks the bfcache.
	 * ------------------------------------------------------------------- */
	const savePosition = () => store.set(SCROLL_KEY, String(window.scrollY));

	window.addEventListener('pagehide', savePosition);
	document.addEventListener('visibilitychange', () => {
		if (document.visibilityState === 'hidden') savePosition();
	});

	// Restored from the back/forward cache: this script does not re-run, so the
	// restore has to be kicked off from here instead. Because scrollRestoration
	// is 'manual' the browser will not reposition the page for us either — doing
	// nothing here is what left mobile Safari (which caches far more eagerly
	// than desktop Chrome) sitting at the top of the collection.
	window.addEventListener('pageshow', event => {
		if (!event.persisted) return;
		cameFromCache = true;
		start();
	});

	/* ---------------------------------------------------------------------
	 * Track whether the visitor took over. Programmatic scrollTo does not
	 * trigger any of these, so they are a reliable "hands off" signal.
	 *
	 * Deliberately NOT listening for pointerdown/touchstart: on desktop a
	 * pointerdown means a click, but on touch *any* contact fires it —
	 * including the tail of an iOS edge-swipe-back gesture and the tap on the
	 * product card that started this whole trip. A tap is not a scroll.
	 * ------------------------------------------------------------------- */
	let userTookOver = false;
	let surrenderArmed = false;
	const TOUCH_SLOP = 10;  // px of finger travel before it counts as scrolling
	const SCROLL_SLOP = 40; // px the page may drift from where we put it

	// Where our own last scrollTo() left the page. `null` means no restore is
	// running and the position is nobody's business but the visitor's.
	let expectedY = null;

	const surrender = () => {
		userTookOver = true;
		expectedY = null;
	};

	// Every programmatic scroll goes through here. Reading scrollY back rather
	// than trusting the argument matters: the browser clamps to the document
	// height, and a target we could not reach is not a position the visitor moved
	// us away from.
	const setScroll = y => {
		window.scrollTo(0, y);
		expectedY = window.scrollY;
	};

	// A scrollbar drag produces no wheel, key or touch event — in Chrome it does
	// not even dispatch mousedown to the page — so the only way to notice it is
	// that the page ended up somewhere we did not put it. Scroll events are
	// dispatched before the frame's rAF callbacks, so a jump made between two
	// ticks of a restore loop is still measured against that loop's own last
	// position.
	const onForeignScroll = () => {
		if (userTookOver || expectedY === null) return;
		if (Math.abs(window.scrollY - expectedY) > SCROLL_SLOP) surrender();
	};

	let touchStartY = null;
	const onTouchStart = event => {
		touchStartY = event.touches[0] ? event.touches[0].clientY : null;
	};
	const onTouchMove = event => {
		if (touchStartY === null || !event.touches[0]) return;
		if (Math.abs(event.touches[0].clientY - touchStartY) > TOUCH_SLOP) surrender();
	};

	// Re-armable: on a bfcache restore the flag and the listeners from the
	// previous visit are still in memory, and a `once: true` listener that has
	// already fired is gone for good.
	const armSurrender = () => {
		userTookOver = false;
		touchStartY = null;
		expectedY = null;
		if (surrenderArmed) return;
		surrenderArmed = true;
		window.addEventListener('wheel', surrender, { passive: true });
		window.addEventListener('keydown', surrender, { passive: true });
		window.addEventListener('touchstart', onTouchStart, { passive: true });
		window.addEventListener('touchmove', onTouchMove, { passive: true });
		window.addEventListener('scroll', onForeignScroll, { passive: true });
	};

	/* ---------------------------------------------------------------------
	 * Keep the card centred while the page settles. The grid itself is height
	 * stable (card__inner reserves its ratio before images load), but banners,
	 * the announcement bar and the sticky header above it are not — they push
	 * the grid down after a single scrollIntoView() has already run.
	 * ------------------------------------------------------------------- */
	const holdOnCard = card => {
		const desiredY = () => {
			const rect = card.getBoundingClientRect();
			const centred = rect.top + window.scrollY - (window.innerHeight - rect.height) / 2;
			const max = document.documentElement.scrollHeight - window.innerHeight;
			return Math.max(0, Math.min(centred, max));
		};

		const MAX_MS = 4000;   // hard stop
		const STABLE_MS = 600; // stop once the target has held still this long
		const started = performance.now();
		let lastTarget = -1;
		let lastChanged = started;

		const tick = now => {
			if (userTookOver) return;

			const target = desiredY();

			// Track movement of the *target*, not of our own correction. A target
			// the browser cannot reach (clamped by document height, sticky UI)
			// would otherwise keep the loop alive for the full timeout.
			if (target !== lastTarget) {
				lastTarget = target;
				lastChanged = now;
			}

			// Either way the position is now ours: a frame we chose not to correct
			// still has to be recorded, or the next foreign scroll is measured
			// against a stale value.
			if (Math.abs(window.scrollY - target) > 2) setScroll(target);
			else expectedY = window.scrollY;

			if (now - started > MAX_MS || now - lastChanged > STABLE_MS) {
				expectedY = null;
				return;
			}
			requestAnimationFrame(tick);
		};

		requestAnimationFrame(tick);

		// Late web-font swaps can shift the grid after the loop above has ended.
		if (document.fonts && document.fonts.ready) {
			document.fonts.ready.then(() => {
				if (userTookOver) return;
				setScroll(desiredY());
				expectedY = null;
			});
		}
	};

	/* ---------------------------------------------------------------------
	 * Ajaxinate cooperation.
	 *
	 * Ajaxinate is scroll-driven and single-flight: checkIfPaginationInView
	 * unbinds its own scroll listener *before* fetching, writes loadingText
	 * into the link, and only rebinds from the XHR success handler. Anything
	 * that stops that handler from running kills infinite scroll for the rest
	 * of the page's life, with "loading" frozen on screen:
	 *
	 *  - a bfcache restore revives a document whose in-flight XHR was aborted
	 *    when it was frozen, so success never fires. Mobile-only in practice:
	 *    WebKit caches this page eagerly, desktop Chrome mostly re-executes it.
	 *  - our own paging below, appending pages and rewriting
	 *    #AjaxinatePagination underneath a request that is still in flight.
	 *
	 * destroy() + new is safe. A second instance *without* destroying the
	 * first is what double-appends pages.
	 * ------------------------------------------------------------------- */
	let ajaxinateSettings = null;

	const suspendAjaxinate = () => {
		const instance = window.ajaxinateInstance;
		if (!instance) return;
		ajaxinateSettings = instance.settings;
		try { instance.destroy(); } catch (e) { /* nothing bound */ }
		// A late response would append pages behind whatever we do next.
		try { if (instance.request) instance.request.abort(); } catch (e) { /* already settled */ }
	};

	const reviveAjaxinate = () => {
		const live = window.ajaxinateInstance;
		const settings = ajaxinateSettings || (live && live.settings);
		if (!settings || typeof window.Ajaxinate !== 'function') return;
		suspendAjaxinate(); // whatever is live must not outlive the replacement
		window.ajaxinateInstance = new window.Ajaxinate(settings);
		ajaxinateSettings = null;
	};

	/* ---------------------------------------------------------------------
	 * Return to a specific product card.
	 * ------------------------------------------------------------------- */
	const restoreToProduct = (grid, targetPath) => {
		const findCard = () => {
			for (const link of grid.querySelectorAll('a[href*="/products/"]')) {
				if (productPath(link.getAttribute('href')) === targetPath) {
					return link.closest('li') || link;
				}
			}
			return null;
		};

		// Ajaxinate narrows its own request to a single section; this one has to do
		// the same, or walking back to a product on page 6 downloads six complete
		// storefront pages instead of six product grids.
		const sectionUrl = href => {
			const instance = window.ajaxinateInstance;
			const sectionId = (instance && instance.settings && instance.settings.sectionId)
				|| (ajaxinateSettings && ajaxinateSettings.sectionId);
			if (!sectionId) return href;
			try {
				const url = new URL(href, window.location.origin);
				url.searchParams.set('section_id', sectionId);
				return url.href;
			} catch (e) {
				return href;
			}
		};

		// Append the next Ajaxinate page silently. Only safe to call while
		// Ajaxinate is suspended — see reviveAjaxinate() above.
		const loadNextPage = async () => {
			const nextLink = document.querySelector('#AjaxinatePagination a');
			if (!nextLink) return false;
			try {
				const res = await fetch(sectionUrl(nextLink.href));
				if (!res.ok) return false;
				const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
				const newItems = doc.querySelector('#product-grid');
				if (!newItems) return false;
				grid.insertAdjacentHTML('beforeend', newItems.innerHTML);
				const pagination = document.querySelector('#AjaxinatePagination');
				const newPagination = doc.querySelector('#AjaxinatePagination');
				if (pagination) pagination.innerHTML = newPagination ? newPagination.innerHTML : '';
				return true;
			} catch (e) {
				return false;
			}
		};

		(async () => {
			suspendAjaxinate();

			let card = findCard();
			try {
				for (let i = 0; !card && i < 10 && !userTookOver; i++) {
					if (!(await loadNextPage())) break;
					card = findCard();
				}
			} finally {
				// Before the scroll loops below, not after: they run on rAF for
				// seconds and their scroll events are exactly what should be
				// driving autoload again by then.
				reviveAjaxinate();
			}

			// The product can be gone from this grid entirely — unpublished, sold
			// out and hidden, or excluded by the collection's discount filter.
			// Land on the old pixel position rather than doing nothing at all.
			if (card) holdOnCard(card);
			else restorePixelPosition();
		})();
	};

	/* ---------------------------------------------------------------------
	 * Fallback: no specific product, just restore the pixel position.
	 * ------------------------------------------------------------------- */
	const restorePixelPosition = () => {
		const stored = store.get(SCROLL_KEY);
		if (!stored) return;

		if (!isReturnNavigation()) {
			store.remove(SCROLL_KEY);
			return;
		}

		const targetY = Number(stored);
		if (!Number.isFinite(targetY) || targetY <= 0) return;

		const maxY = () => document.documentElement.scrollHeight - window.innerHeight;

		const MAX_MS = 6000;   // hard stop
		const GROW_MS = 1500;  // give up once the document stops getting taller
		const started = performance.now();
		let lastMax = -1;
		let lastGrew = started;

		const tick = now => {
			if (userTookOver) return;
			if (now - started > MAX_MS) {
				expectedY = null;
				return;
			}

			setScroll(targetY);
			if (Math.abs(window.scrollY - targetY) <= 2) {
				expectedY = null;
				return;
			}

			// Target is below the current document end — we are waiting for
			// Ajaxinate to append more. Stop once nothing is being added, or
			// this keeps calling scrollTo every frame for the full timeout.
			const height = maxY();
			if (height !== lastMax) {
				lastMax = height;
				lastGrew = now;
			}
			if (now - lastGrew > GROW_MS) {
				expectedY = null;
				return;
			}

			requestAnimationFrame(tick);
		};

		requestAnimationFrame(tick);
	};

	let cardTrackerBound = false;

	const start = () => {
		const grid = document.getElementById('product-grid');

		// Fresh listeners for every attempt — see armSurrender().
		armSurrender();

		if (grid) {
			// Remember which card was clicked. pointerdown in the capture phase
			// survives handlers that stopPropagation or re-render the card.
			// Bound once: start() runs again on a bfcache restore, when the
			// listener from the first execution is still attached.
			if (!cardTrackerBound) {
				cardTrackerBound = true;
				document.addEventListener('pointerdown', event => {
					// Right- and middle-click open a new tab without navigating away.
					// Storing those would teleport the visitor on some later visit.
					if (event.button !== 0 || !event.isPrimary) return;
					const link = event.target.closest && event.target.closest('a[href*="/products/"]');
					if (link && grid.contains(link)) store.set(RETURN_KEY, link.href);
				}, true);
			}

			const returnTo = store.get(RETURN_KEY);
			if (returnTo) {
				store.remove(RETURN_KEY);
				restoreToProduct(grid, productPath(returnTo));
				return;
			}

			// A revived document can carry a half-finished Ajaxinate request that
			// will never complete, and restorePixelPosition() below depends on
			// autoload to reach an offset past the current document end.
			if (cameFromCache) reviveAjaxinate();
		}

		restorePixelPosition();
	};

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', start, { once: true });
	} else {
		start();
	}
}());
